import { type App, ref, watch } from 'vue'
import { useEscListener } from '@/functions'
import AppModal from './app-modal.vue'
import AppModalContent from './app-modal-content.vue'
import AppModalFooter from './app-modal-footer.vue'
import AppModalForm from './app-modal-form.vue'
import AppModalTitle from './app-modal-title.vue'

export function useModal () {
  const isOpen = ref(false)

  const open = () => {
    isOpen.value = true
  }
  const close = () => {
    isOpen.value = false
  }

  const onEscape = useEscListener(close)

  watch(isOpen, (value) => {
    if (value) {
      window.addEventListener('keydown', onEscape)
    } else {
      window.removeEventListener('keydown', onEscape)
    }
  })

  return { isOpen, open, close }
}

type Modalable = GModal
export function useAppModal<T extends unknown[]> (onBeforeOpen?: (...args: T) => void) {
  const modal = ref<Modalable>()
  // calling open with any arguments will be passed to onModalOpened callback
  const open = (...args: T) => {
    onBeforeOpen?.(...args)
    modal.value?.open()
  }
  const close = () => {
    modal.value?.close()
  }
  return { modal, open, close }
}

export default {
  install (app: App) {
    app.component('GModal', AppModal)
    app.component('GModalContent', AppModalContent)
    app.component('GModalForm', AppModalForm)
    app.component('GModalFooter', AppModalFooter)
    app.component('GModalTitle', AppModalTitle)
  },
}

declare module '@vue/runtime-core' {
  export interface GlobalComponents {
    GModal: typeof AppModal,
    GModalContent: typeof AppModalContent,
    GModalFooter: typeof AppModalFooter,
    GModalForm: typeof AppModalForm,
    GModalTitle: typeof AppModalTitle,
  }
}

declare global {
  type GModal = InstanceType<typeof AppModal>
}
